// Shared "Dynamic (AI-powered)" content recipe builders — used by both the
// New Topic modal (views/course.js, choosing Dynamic at creation) and the
// Studio's "Make dynamic" conversion flow (views/studio.js, adding it to an
// existing static topic). Pulled out to its own lib module rather than
// living in one view and being imported by the other, since neither builder
// touches the DOM — pure string-building, the same shape as any other
// lib/*.js helper.
//
// Two separate pieces, mirroring the "comment pins" pattern already used in
// the Studio (see buildInstructionWithPins in studio.js): a plain-language
// DRAFT the creator actually sees and edits in the chat box, and a technical
// implementation RECIPE that's folded into the instruction invisibly the
// next time they hit Send (see studio.js's foldInDynamicRecipe) — the
// creator never has to read or approve the technical how-to, only what they
// want built. Nothing auto-sends either way; generation (and its AI cost)
// only happens once they click Send themselves.

// The plain-language message the creator sees pre-filled in the Studio chat
// box — no mention of window.askAI or any implementation detail.
export function buildDynamicUserDraft({ aiContent, processing }) {
  const parts = [];
  if (aiContent) parts.push(`get the AI to provide ${aiContent}`);
  if (processing) parts.push(processing);
  return `Build this topic: ${parts.join(', then ')}.`;
}

// Same idea, phrased for a topic that already has a generated page (the
// Studio's "Make dynamic" button) rather than one being built from scratch.
export function buildDynamicConversionDraft({ aiContent, processing }) {
  const parts = [];
  if (aiContent) parts.push(`get the AI to provide ${aiContent}`);
  if (processing) parts.push(processing);
  return parts.length
    ? `Update this page to use live AI content: ${parts.join(', then ')}.`
    : 'Update this page so its content is fetched live from the AI instead of being fixed.';
}

// The technical half — never shown to the creator (folded in invisibly, see
// studio.js's handleSend/foldInDynamicRecipe). Spells out the exact AI
// bridge usage pattern AND a concrete code skeleton, not just prose, because
// prose alone was unreliable (a generated "New passage"-style button not
// responding to clicks is almost always the model wiring the click handler
// wrong or attaching it before the element exists — the skeleton below
// forces the one pattern that avoids both).
//
// aiContent/processing are both optional — leaving them blank still
// produces a coherent, generic recipe (via the fallback text below), it
// just doesn't narrow down WHAT the AI should provide. This must be folded
// in unconditionally whenever a topic is Dynamic, not only when these two
// fields are filled in: skipping it on empty fields is what let a topic
// marked Dynamic still generate as an ordinary static page with no idea it
// was ever supposed to call window.askAI.
export function buildDynamicRecipe({ aiContent, processing }) {
  const contentLine = aiContent || 'the content this page needs from the AI';
  const processingLine = processing || 'what the page should do with that content';
  return `Implementation requirements for the AI-provided content described above (AI should provide: ${contentLine}; page should then: ${processingLine}) — this must happen invisibly in the background from the learner's point of view, no debug/technical text shown to them:

Use exactly this structure (adapt element ids/names and the actual question text, but keep the async/await + try/catch + DOM-ready shape):

<script>
document.addEventListener('DOMContentLoaded', function () {
  var btn = document.getElementById('generate-btn'); // give your trigger button this id (or update this selector to match it exactly)
  btn.addEventListener('click', async function () {
    btn.disabled = true;
    var originalLabel = btn.textContent;
    btn.textContent = 'Thinking…';
    try {
      var content = await window.askAIContent('comprehension', {
        topic: '<the learner/topic text>',
        words: 200,
        questions: 4,
        level: 'intermediate'
      });
      // render content.passage and content.questions into the interactive experience here
    } catch (err) {
      // show a short inline error near the button, e.g. set a status element's text — do not use alert()
    } finally {
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  });
});
</script>

Rules:
1. The button must exist in the HTML and its id/selector in the script must match exactly — double-check this before returning.
2. Attach the click listener only after the DOM is ready (DOMContentLoaded, or a script placed at the end of <body>) — a listener attached to an element that doesn't exist yet is the most common reason such a button silently does nothing.
3. Call exactly one AI bridge method per click, never automatically on page load or in a loop.
4. On failure, show a short inline message (not alert/confirm — those are blocked in this sandboxed iframe) and re-enable the button.
5. CRITICAL — if the page needs JSON, a passage with questions, a generated quiz/practice set, or anything else that must be parsed/rendered as DATA, use window.askAIContent(kind, params), not window.askAI(). Never ask window.askAI() for JSON and never parse AI prose as JSON inside the iframe.
6. CRITICAL — if "AI should provide" above states an explicit length, word count, or number of items (e.g. "1000 words", "5 examples", "3 paragraphs"), carry that number into the askAIContent params when a structured kind supports it, or into the literal question string when and only when using window.askAI() for plain text. Do not paraphrase, drop, or soften the number.

CORRECTNESS POLICY — decide this yourself based on what's being checked/scored; never surface this decision to the learner as a choice or as any visible technical detail:
- Default: if what counts as "correct" is a fixed, discrete value knowable at the moment the content is generated (the right verb tense, the right multiple-choice option, a right/wrong numeric answer, etc.), prefer a structured window.askAIContent kind when one fits. If no structured kind fits, keep any window.askAI question concise and ask for a simple inline machine-parseable format of your choosing — e.g. request the passage with each checkable spot written as '{correct form|distractor 1|distractor 2}'. Parse that small format out with your own JS immediately after the answer arrives (before rendering anything): extract the bracketed options to build the blank/dropdown and remember the first option as correct, then strip the markup so the learner only ever sees natural text and dropdowns — never raw braces, never a second AI call just to learn what's correct, and score entirely client-side from what you already parsed.
- Only fall back to a second window.askAI call, made at the moment the learner submits/checks a specific answer, when correctness genuinely requires open-ended judgment that cannot be fixed at generation time (grading a free-text explanation, evaluating an essay, etc.). That second call gets the same loading-state + try/catch treatment as the first.
- Whichever path you use, the learner's experience must feel identical: they see a natural page, interact with it, and get instant feedback — they must never see or need to know which of the two approaches is happening underneath.`;
}

// Wraps the same technical recipe with one extra sentence of framing for
// the "existing static page" case — the skeleton/rules above are identical
// either way, only the surrounding context differs (build-from-scratch vs.
// edit-in-place).
export function buildDynamicConversionRecipe({ aiContent, processing }) {
  return `This existing page currently has static/fixed content that should become live instead — edit the page in place rather than rebuilding it from scratch, replacing only the relevant static content with the live version described below.\n\n${buildDynamicRecipe({ aiContent, processing })}`;
}
